import React, {useEffect, useState, useRef} from 'react';
import {createFragmentContainer, graphql} from 'react-relay';
import {injectIntl} from 'react-intl';
import styled from 'styled-components';
import {Alert} from '@forecast-it/design-system';
import {useHistory} from 'react-router-dom';
import {CSS_CONSTANTS} from '../../../css_variables';
import {RateCardsV2} from './settings_finance_rate_cards_v2';
import {ExpenseCategories} from './settings_finance_expense_categories';
import {CurrenciesOld} from './settings_finance_currencies';
import {Currencies} from './SettingsFinanceCurrencies';
import {Invoicing} from './settings_finance_invoicing';
import CustomScrollDiv from '../../shared/components/scroll-bars/custom_scroll_div';
import {Tab} from './tabs/tab';
import {Tabs} from './tabs/tabs';
import {TopHeaderBar, TopHeaderBarWrapper} from '../../shared/components/headers/top-header-bar/TopHeaderBar';
import {hasModule} from '../../shared/util/ModuleUtil';
import {hasFeatureFlag} from '../../shared/util/FeatureUtil';
import {HIDDEN_FEATURES, MODULE_TYPES} from '../../../constants';
import {BudgetTolerance} from './SettingsFinanceBudgetTolerance';
import CompanySetupUtil, {isFeatureHidden} from '../../shared/util/CompanySetupUtil';

const SettingsFinanceStyles = styled.div`
	.inner {
		padding: 16px 36px 50px;
		.header {
			display: flex;
			.finance-headline {
				flex-grow: 1;
			}
			.custom-button-container {
				height: 30px;
			}
		}
	}

	${({useLegacyStyles}) =>
		useLegacyStyles &&
		`
		h1 {
			font-size: 24px;
			font-family: 'neue-haas-grotesk-text', sans-serif;
			margin-bottom: 0;
			padding-bottom: 20px;
		}

		h2 {
			font-size: 20px;
			font-family: 'neue-haas-grotesk-text', sans-serif;
			font-weight: 500;
			margin-top: 32px;
			margin-bottom: 0;
		}

		.description {
			width: 50%;
			font-size: 13px;
			margin-top: 16px;
			margin-bottom: 0;
		}

		table {
			margin-top: 24px;
			width: 100%;

			thead {
				th {
					font-size: 9px;
					text-transform: uppercase;
					font-weight: 500;
					color: ${CSS_CONSTANTS.v2_text_light_gray};
					text-align: left;
					letter-spacing: 1px;
					border-bottom: 1px solid ${CSS_CONSTANTS.v2_button_very_light_grey};
					height: 30px;

					.header-wrapper {
						display: flex;
						align-items: center;
					}
				}
			}

			tbody {
				tr {
					td {
						height: 48px;
						border-bottom: 1px solid ${CSS_CONSTANTS.v2_button_very_light_grey};
						color: ${CSS_CONSTANTS.v2_text_gray};
						font-size: 13px;
						vertical-align: center;
					}

					&:hover {
						td {
							background-color: ${CSS_CONSTANTS.v2_list_background_purple};
						}
					}
				}
			}
		}
	`}
`;

const TABS = {
	RATE_CARDS: 'rate-cards',
	EXPENSE_CATEGORIES: 'expense-categories',
	CURRENCIES: 'currencies',
	BUDGET_TOLERANCE: 'budget-tolerance',
	INVOICING: 'invoicing',
};

const getDefaultTab = hash => {
	if (hash) {
		const hashTab = hash.replace('#', '');
		if (Object.values(TABS).some(tab => tab === hashTab)) {
			return hashTab;
		}
	}

	return isFeatureHidden(HIDDEN_FEATURES.REVENUE) ? TABS.EXPENSE_CATEGORIES : TABS.RATE_CARDS;
};

const settingsFinance = ({intl: {formatMessage}, viewer: {company, projects}}) => {
	const history = useHistory();
	const useDateEffectiveCurrencyExchangeRatesFF = hasFeatureFlag('date_effective_currency_exchange_rates');
	const hasNovaInsights = hasModule(MODULE_TYPES.NOVA_INSIGHTS);
	const [showCurrencySuccessAlert, setShowCurrencySuccessAlert] = useState(false);
	const [showCurrencyDeleteAlert, setShowCurrencyDeleteAlert] = useState(false);
	const rateCards = company.rateCards.edges.map(edge => edge.node);
	const expenseCategories = company.expenseCategories.edges.map(edge => edge.node);
	const exchangeRates = company.exchangeRates.edges.map(edge => edge.node);
	projects = projects.edges.map(edge => edge.node);

	const invoicingIntegrationEnabled = company.newXeroEnabled || company.economicEnabled || company.quickbooksEnabled;

	const showRateCardsTab = !isFeatureHidden(HIDDEN_FEATURES.ADMIN_FINANCE_RATE_CARD);
	const showExpenseCategoriesTab = CompanySetupUtil.hasCreateExpense();
	const showCurrenciesTab = !isFeatureHidden(HIDDEN_FEATURES.ADMIN_FINANCE_CURRENCIES);
	const showBudgetToleranceTab =
		CompanySetupUtil.hasFinance() && !isFeatureHidden(HIDDEN_FEATURES.ADMIN_FINANCE_BUDGET_TOLERANCE) && hasNovaInsights;
	const showInvoicingTab = CompanySetupUtil.hasCreateInvoice() && !isFeatureHidden(HIDDEN_FEATURES.ADMIN_FINANCE_INVOICING);

	useEffect(() => {
		document.title = 'Finance - Account - Forecast';
	}, []);

	const defaultTab = getDefaultTab(history.location.hash);
	const useLegacyStyles = useRef(!(defaultTab === TABS.CURRENCIES && useDateEffectiveCurrencyExchangeRatesFF));

	const onSelectTab = tabIndex => {
		const newTabSelected = Object.values(TABS)[tabIndex];
		location.hash = newTabSelected;
		useLegacyStyles.current = !(newTabSelected === TABS.CURRENCIES && useDateEffectiveCurrencyExchangeRatesFF);
	};

	return (
		<div className="section-content settings-app settings-finance" data-cy={'settings-finance-page'}>
			<TopHeaderBarWrapper sidePadding={24}>
				<TopHeaderBar title={formatMessage({id: 'settings_finance.menuTitle'})} />
			</TopHeaderBarWrapper>
			<CustomScrollDiv>
				<SettingsFinanceStyles useLegacyStyles={useLegacyStyles.current}>
					<div className={useLegacyStyles.current ? 'section-body' : ''}>
						<div className="inner">
							{showCurrencySuccessAlert && (
								<Alert
									text={formatMessage({id: 'settings_finance.currencies.alert.currency_added_alert'})}
									type={Alert.TYPE.SUCCESS}
									onDismiss={() => setShowCurrencySuccessAlert(false)}
								/>
							)}
							{showCurrencyDeleteAlert && (
								<Alert
									text={formatMessage({id: 'settings_finance.currencies.alert.currency_deleted_alert'})}
									type={Alert.TYPE.SUCCESS}
									onDismiss={() => setShowCurrencyDeleteAlert(false)}
								/>
							)}
							<Tabs onTabSelect={onSelectTab}>
								{showRateCardsTab ? (
									<Tab
										key={TABS.RATE_CARDS}
										defaultSelected={defaultTab === TABS.RATE_CARDS}
										title={formatMessage({id: 'common.rate-cards'})}
									>
										<RateCardsV2 company={company} projects={projects} rateCards={rateCards} />
									</Tab>
								) : (
									<></>
								)}
								{showExpenseCategoriesTab ? (
									<Tab
										key={TABS.EXPENSE_CATEGORIES}
										defaultSelected={defaultTab === TABS.EXPENSE_CATEGORIES}
										title={formatMessage({id: 'common.expense-categories'})}
									>
										<ExpenseCategories company={company} expenseCategories={expenseCategories} />
									</Tab>
								) : (
									<></>
								)}
								{showCurrenciesTab ? (
									<Tab
										key={TABS.CURRENCIES}
										defaultSelected={defaultTab === TABS.CURRENCIES}
										title={formatMessage({id: 'settings.currencies'})}
									>
										{useDateEffectiveCurrencyExchangeRatesFF ? (
											<Currencies
												company={company}
												exchangeRates={exchangeRates}
												setShowCurrencySuccessAlert={value => setShowCurrencySuccessAlert(value)}
												setShowCurrencyDeleteAlert={value => setShowCurrencyDeleteAlert(value)}
											/>
										) : (
											<CurrenciesOld company={company} exchangeRates={exchangeRates} />
										)}
									</Tab>
								) : (
									<></>
								)}
								{showBudgetToleranceTab ? (
									<Tab
										key={TABS.BUDGET_TOLERANCE}
										defaultSelected={defaultTab === TABS.BUDGET_TOLERANCE}
										title={'Budget Tolerance'}
									>
										<BudgetTolerance company={company} />
									</Tab>
								) : (
									<></>
								)}
								{showInvoicingTab ? (
									<Tab
										key={TABS.INVOICING}
										defaultSelected={defaultTab === TABS.INVOICING}
										title={formatMessage({id: 'common.invoicing'})}
									>
										<Invoicing
											company={company}
											invoicingIntegrationEnabled={invoicingIntegrationEnabled}
										/>
									</Tab>
								) : (
									<></>
								)}
							</Tabs>
						</div>
					</div>
				</SettingsFinanceStyles>
			</CustomScrollDiv>
		</div>
	);
};

export const settingsFinanceQuery = graphql`
	query settingsFinance_Query {
		viewer {
			actualPersonId
			component(name: "settings_finance")
			...settingsFinance_viewer
		}
	}
`;

export default injectIntl(
	createFragmentContainer(settingsFinance, {
		viewer: graphql`
			fragment settingsFinance_viewer on Viewer {
				id
				availableFeatureFlags {
					key
				}
				company {
					id
					tier
					forecastDemo
					currency
					defaultRevenueMarginLow
					defaultRevenueMarginHigh
					invoiceQuantityDecimals
					defaultRateCard {
						id
					}
					defaultExpenseCategory {
						id
					}
					exchangeRates(first: 10000) @connection(key: "Company_exchangeRates") {
						edges {
							node {
								id
								currency
								rate
								isUsed
								createdAt
							}
						}
					}
					rateCards(first: 10000) @connection(key: "Company_rateCards") {
						edges {
							node {
								id
								name
								defaultRate
								parentRateCardId
								currency
								disabled
								isRateCardUsed
								disabledRoles {
									id
								}
								rates(first: 10000) {
									edges {
										node {
											id
											rate
											startDate
											defaultRate
											role {
												id
												name
											}
										}
									}
								}
							}
						}
					}
					expenseCategories(first: 10000) @connection(key: "Company_expenseCategories") {
						edges {
							node {
								id
								name
								disabled
								baselineUses
								canDelete
							}
						}
					}
					newXeroEnabled
					economicEnabled
					quickbooksEnabled
					integrations {
						userCanUnlinkInvoice
					}
					billFroms {
						billTos(first: 10000) @connection(key: "BillFrom_billTos", filters: []) {
							edges {
								node {
									id
								}
							}
						}
					}
				}
				projects(first: 1000000) {
					edges {
						node {
							id
							companyProjectId
							hasLockedPeriod
							hasInvoices
							rateCard {
								id
							}
						}
					}
				}
			}
		`,
	})
);
